import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute, PublicRoute, PermissionRoute } from "../guards";
import { Dashboard } from "../pages";
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  JoinPoolPage,
  PasswordResetOpenPage,
  NotFoundPage,
} from "../pages";
import { UsersListPage, CreateUserPage, UpdateUserPage } from "../pages/users";
import { RolesListPage, CreateRolePage } from "../pages/roles";
import {
  TournamentListPage,
  CreateTournamentPage,
  UpdateTournamentPage,
  TournamentDetailPage,
} from "../pages/tournaments";
import {
  DisciplinesListPage,
  UpdateDisciplinePage,
} from "../pages/disciplines";
import {
  PrizeTypesListPage,
  CreatePrizeTypePage,
  UpdatePrizeTypePage,
} from "../pages/prize-types";
import {
  TournamentFormatsListPage,
  CreateTournamentFormatPage,
  UpdateTournamentFormatPage,
} from "../pages/tournament-formats";
import {
  SubCategoriesListPage,
  CreateSubCategoryPage,
  UpdateSubCategoryPage,
} from "../pages/sub-categories";
import {
  MatchTeamsListPage,
  CreateMatchTeamPage,
  UpdateMatchTeamPage,
} from "../pages/match-teams";
import { MatchesListPage } from "../pages/matches";
import { ProtectedLayout } from "../components/layout";
import { UsersPermissionsGuardEnum, RolesGuardEnum, AdminGuardEnum } from "@/core/enums";
import { UpdateRolePage } from "../pages/roles/UpdateRolePage";
import { CreateBettingPoolPage } from "../pages/sports-predictions/CreateBettingPoolPage";
import { EditBettingPoolPage } from "../pages/sports-predictions/EditBettingPoolPage";
import { MyBettingPoolsPage } from "../pages/sports-predictions/MyBettingPoolsPage";
import { BettingPoolDetailPage } from "../pages/sports-predictions/BettingPoolDetailPage";
import { PoolPredictionsPage } from "../pages/sports-predictions/PoolPredictionsPage";

export function AppRouter() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Rutas públicas */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />
        <Route path="/join" element={<JoinPoolPage />} />
        <Route path="/invitations/open/:token" element={<JoinPoolPage />} />
        <Route path="/password-reset" element={<PasswordResetOpenPage />} />
        <Route path="/password-reset/open/:token" element={<PasswordResetOpenPage />} />

        {/* Rutas privadas con layout compartido */}
        <Route
          element={
            <ProtectedRoute>
              <ProtectedLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<Dashboard />} />
          <Route
            path="/users"
            element={
              <PermissionRoute
                permissions={[UsersPermissionsGuardEnum.USERS_VIEW]}
              >
                <UsersListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/users/create"
            element={
              <PermissionRoute
                permissions={[UsersPermissionsGuardEnum.USERS_CREATE]}
              >
                <CreateUserPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/users/edit/:id"
            element={
              <PermissionRoute
                permissions={[UsersPermissionsGuardEnum.USERS_UPDATE]}
              >
                <UpdateUserPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <PermissionRoute permissions={[RolesGuardEnum.ROLES_VIEW]}>
                <RolesListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/roles/create"
            element={
              <PermissionRoute permissions={[RolesGuardEnum.ROLES_CREATE]}>
                <CreateRolePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/roles/edit/:id"
            element={
              <PermissionRoute permissions={[RolesGuardEnum.ROLES_UPDATE]}>
                <UpdateRolePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tournaments"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <TournamentListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tournaments/create"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <CreateTournamentPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tournaments/edit/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <UpdateTournamentPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tournaments/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <TournamentDetailPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/disciplines"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <DisciplinesListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/disciplines/edit/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <UpdateDisciplinePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/prize-types"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <PrizeTypesListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/prize-types/create"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <CreatePrizeTypePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/prize-types/edit/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <UpdatePrizeTypePage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tournament-formats"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <TournamentFormatsListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tournament-formats/create"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <CreateTournamentFormatPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/tournament-formats/edit/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <UpdateTournamentFormatPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/sub-categories"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <SubCategoriesListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/sub-categories/create"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <CreateSubCategoryPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/sub-categories/edit/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <UpdateSubCategoryPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/match-teams"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <MatchTeamsListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/match-teams/create"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <CreateMatchTeamPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/match-teams/edit/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <UpdateMatchTeamPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/matches"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <MatchesListPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/sports-predictions/pools/create"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <CreateBettingPoolPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/sports-predictions/pools/edit/:id"
            element={
              <PermissionRoute permissions={[AdminGuardEnum.ADMIN_ACCESS]}>
                <EditBettingPoolPage />
              </PermissionRoute>
            }
          />
          <Route
            path="/sports-predictions/pools/my-pools"
            element={<MyBettingPoolsPage />}
          />
          <Route
            path="/sports-predictions/pools/joined"
            element={<Navigate to="/sports-predictions/pools/my-pools" replace />}
          />
          <Route
            path="/sports-predictions/pools/:id"
            element={<BettingPoolDetailPage />}
          />
          <Route
            path="/sports-predictions/pools/:id/predictions"
            element={<PoolPredictionsPage />}
          />
          {/* Ruta 404 - debe estar al final para capturar todas las rutas no definidas */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Redirect por defecto */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
